import { useCallback, useEffect, useState } from "react";
import { http } from "../services/http";

// /hours-last-week e /sprints-inconsistencies respondem ora com lista, ora
// com um objeto solto quando há uma única ocorrência.
function readList(response) {
  const data = response?.data?.data;
  if (Array.isArray(data)) return data;
  return data ? [data] : [];
}

const EMPTY_RESULT = { items: [], groups: [], projectLevelItems: [], error: null };

export function useInconsistencies({ projectId, sprintIds }) {
  // A chave derivada é o que impede o loop infinito de requisições: um array
  // no vetor de dependências ganha identidade nova a cada render e o efeito
  // se refaria para sempre. Os ids voltam a ser lista dentro do efeito.
  const sprintKey = (sprintIds || []).join(",");

  const [reloadToken, setReloadToken] = useState(0);
  const requestKey = `${projectId}|${sprintKey}|${reloadToken}`;

  const [snapshot, setSnapshot] = useState({ key: null, ...EMPTY_RESULT });

  // "Carregando" é o resultado ainda não corresponder à requisição atual, e
  // não uma flag que o efeito liga: trocar de sprint muda a chave no mesmo
  // render, sem o quadro em que a tela mostra o resultado antigo como final.
  const loading = sprintKey.length > 0 && snapshot.key !== requestKey;

  const reload = useCallback(() => setReloadToken((token) => token + 1), []);

  useEffect(() => {
    const selectedIds = sprintKey ? sprintKey.split(",") : [];

    if (selectedIds.length === 0) {
      setSnapshot({ key: requestKey, ...EMPTY_RESULT });
      return undefined;
    }

    let cancelled = false;

    // Uma requisição de tarefas por sprint e uma de cada família
    // project-scoped: elas descartam o versionId, e pedi-las por sprint
    // contava cada documento tantas vezes quantas sprints selecionadas.
    Promise.all([
      Promise.all(
        selectedIds.map((sprintId) =>
          http
            .get("/tasks-inconsistencies", { params: { projectId, versionId: sprintId } })
            .then((response) => ({
              sprintId,
              items: readList(response).map((item) => ({ ...item, _sprintId: sprintId })),
            })),
        ),
      ),
      http.get("/documentations-inconsistencies", { params: { projectId } }),
      http.get("/sprints-inconsistencies", { params: { projectId } }),
      http.get("/hours-last-week", { params: { projectId } }),
    ])
      .then(([groups, documents, sprints, hours]) => {
        if (cancelled) return;

        const projectLevelItems = [
          ...readList(documents),
          ...readList(sprints),
          ...readList(hours),
        ];

        setSnapshot({
          key: requestKey,
          error: null,
          groups,
          projectLevelItems,
          items: [...projectLevelItems, ...groups.flatMap((group) => group.items)],
        });
      })
      .catch((requestError) => {
        if (cancelled) return;

        setSnapshot({ key: requestKey, ...EMPTY_RESULT, error: requestError });
      });

    return () => {
      cancelled = true;
    };
  }, [projectId, sprintKey, requestKey]);

  const { key, ...result } = snapshot;
  return { ...result, loading, reload };
}
